import { Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { CollectionGroupDetailsDto } from "../../../../../../common/service/server/dto/CollectionGroupDto";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { CollectionGroupCollapseCard } from "../../../../../components/CollectionGroupCollapseCard";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import { DataRecipientSourceCheckboxes } from "../../../../../components/organisms/data-sources/DataRecipientSourceCheckboxes";
import { usePrimaryOrganizationId } from "../../../../../hooks";
import { useCollectionGroups } from "../../../../../hooks/useCollectionGroups";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useOrgDataSource } from "../../../../../hooks/useOrganizationVendors";
import { RouteHelpers, Routes } from "../../../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";

type Params = {
  dataSourceId: UUIDString;
};

const CollectionGroupReceiptCard = ({
  dataMap,
  patchDataMap,
  collectionGroup,
  dataSource,
  collapsed,
  onExpand,
  onNext,
}) => {
  return (
    <CollectionGroupCollapseCard
      className="mb-md"
      collectionGroup={collectionGroup}
      collapsed={collapsed}
      onExpand={onExpand}
    >
      <DataRecipientSourceCheckboxes
        source={dataSource}
        dataMap={dataMap}
        patchDataMap={patchDataMap}
        group={collectionGroup}
        includeSelectAll={dataSource.vendorKey === "source-consumer-directly"}
      />

      {Boolean(onNext) && (
        <Button variant="contained" onClick={onNext}>
          Next Group
        </Button>
      )}
    </CollectionGroupCollapseCard>
  );
};

export const DataReceiptForSourcePage = () => {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const { dataSourceId } = useParams<Params>();

  const {
    data: dataMap,
    request: dataMapRequest,
    mutators: { patch },
  } = useDataMap(orgId);
  const [dataSource, dataSourceRequest] = useOrgDataSource(orgId, dataSourceId);
  const [collectionGroups, collectionGroupsRequest] = useCollectionGroups(orgId);

  const [activeGroup, setActiveGroup] = useState<CollectionGroupDetailsDto>(null);

  const associations = useMemo(
    () => dataMap.sourceAssociations.filter((a) => a.vendorId == dataSource?.vendorId),
    [dataMap.sourceAssociations, dataSource],
  );

  const groupIds = useMemo(() => associations.map((d) => d.groupId), [associations]);

  const relevantGroups = useMemo(
    () => collectionGroups?.filter((r) => groupIds?.includes(r.id)),
    [collectionGroups, groupIds],
  );

  const handleExpand = (cg: CollectionGroupDetailsDto) => {
    setActiveGroup(cg);

    window.polaris.scrollSelectorIntoView(`#cg-${cg.id}`);
  };

  const allSelected = useMemo(() => {
    return relevantGroups.every((cg) =>
      dataMap.received.some((d) => d.groupId == cg.id && d.vendorId == dataSource?.vendorId),
    );
  }, [relevantGroups, dataSource, dataMap.received]);

  useEffect(() => {
    if (relevantGroups[0] && !activeGroup) {
      setActiveGroup(relevantGroups[0]);
    }
  }, [activeGroup, relevantGroups]);

  return (
    <StandardPageWrapper
      requests={[collectionGroupsRequest, dataMapRequest, dataSourceRequest]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextOnClick={() => history.push(Routes.dataMap.root)}
              nextContent="Done"
              nextDisabled={!allSelected || patch.isLoading}
              prevOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.dataSource.workflow.sourceAssociation(dataSourceId),
                )
              }
            />
          }
        />
      }
    >
      <PageHeader titleContent="Add Data Source" />

      <Typography className="mb-lg">
        For each of your Collection Groups, identify which categories of personal data are sourced
        from <strong>{dataSource?.name}</strong>.
      </Typography>

      {relevantGroups.map((cg, idx) => {
        let nextGroup: CollectionGroupDetailsDto | null = null;
        if (idx < relevantGroups.length - 1) {
          nextGroup = relevantGroups[idx + 1];
        }
        return (
          <div key={cg.id} id={`cg-${cg.id}`} className="mb-md">
            <CollectionGroupReceiptCard
              dataSource={dataSource}
              dataMap={dataMap}
              patchDataMap={patch}
              collectionGroup={cg}
              collapsed={activeGroup?.id !== cg.id}
              onExpand={() => handleExpand(cg)}
              onNext={nextGroup ? () => handleExpand(nextGroup) : undefined}
            />
          </div>
        );
      })}
    </StandardPageWrapper>
  );
};
